'use client';
import { useState } from 'react';
import { StockFactors, PillarScores } from '@/lib/compute';
import { ALL_QUALITY_KEYS, ALL_VALUATION_KEYS } from '@/lib/clientScore';
import { getSectorColor } from '@/lib/sector-colors';
import { MarketCapSlider } from './control-panel/MarketCapSlider';
import { StatsGrid } from './control-panel/StatsGrid';
import { SearchBar } from './control-panel/SearchBar';
import { PortfolioPanel } from './control-panel/PortfolioPanel';
import { AlphaZonePanel } from './control-panel/AlphaZonePanel';
import { PillarWeightsPanel } from './control-panel/PillarWeightsPanel';
import { SectorsPanel } from './control-panel/SectorsPanel';
import { QualityFactorsPanel } from './control-panel/QualityFactorsPanel';
import { ValuationFactorsPanel } from './control-panel/ValuationFactorsPanel';
import { MomentumFactorsPanel } from './control-panel/MomentumFactorsPanel';
import { PortfolioEventsFeed } from '@/components/PortfolioEventsFeed';

// Re-exported so existing `import { getSectorColor } from '@/components/ControlPanel'` keeps working.
export { getSectorColor };

interface Props {
  sectors: string[];
  selectedSectors: Set<string>;
  onSectorToggle: (s: string) => void;
  selectedIndustries: Set<string>;
  onIndustryToggle: (industry: string) => void;
  /** Reset to "all sectors selected" + clear any industry filter. */
  onSelectAllSectors: () => void;
  minMarketCap: number;
  maxMarketCap: number;
  onMinMarketCapChange: (v: number) => void;
  onMaxMarketCapChange: (v: number) => void;
  search: string;
  onSearch: (s: string) => void;
  count: number;
  factorCount: number;
  /** Canonical Q + V counts from data/model-stats.json. Used by the
   *  QUALITY FACTORS and VALUATION FACTORS panel headers so the badges
   *  match the deck and /rating page. Don't hardcode counts. */
  qualityFactorCount?: number;
  valuationFactorCount?: number;
  updatedAt: string;
  viewMode: 'heatmap' | 'table' | 'backtest' | 'technical' | '3d';
  onViewModeChange: (mode: 'heatmap' | 'table' | 'backtest' | 'technical' | '3d') => void;
  enabledFactors: Set<keyof StockFactors>;
  onToggleFactor: (key: keyof StockFactors) => void;
  onResetFactors: () => void;
  portfolioSymbols: Set<string>;
  onPortfolioChange: (symbols: Set<string>) => void;
  portfolioIsolated: boolean;
  onTogglePortfolioIsolate: () => void;
  alphaZoneSize: number;
  alphaZoneIsolated: boolean;
  onToggleAlphaZoneIsolate: () => void;
  pillarWeights: Record<keyof PillarScores, number>;
  onPillarWeightChange: (pillar: keyof PillarScores, value: number) => void;
  onResetWeights: () => void;
  onPillarClick?: (pillar: string) => void;
  allStocks?: Array<{ symbol: string; name: string }>;
  industriesBySector: Record<string, string[]>;
  onCloseMobile?: () => void;
  highlightedFactor?: keyof StockFactors | null;
  onFactorHighlight?: (key: keyof StockFactors) => void;
  highlightedPillar?: keyof PillarScores | null;
  onPillarHighlight?: (pillar: keyof PillarScores) => void;
}

const allDefaultEnabled = new Set([...ALL_QUALITY_KEYS, ...ALL_VALUATION_KEYS]);

export function ControlPanel({
  sectors, selectedSectors, onSectorToggle,
  selectedIndustries, onIndustryToggle,
  onSelectAllSectors,
  minMarketCap, maxMarketCap, onMinMarketCapChange, onMaxMarketCapChange,
  search, onSearch,
  count, factorCount, qualityFactorCount, valuationFactorCount, updatedAt,
  viewMode: _viewMode, onViewModeChange: _onViewModeChange,
  enabledFactors, onToggleFactor, onResetFactors,
  portfolioSymbols, onPortfolioChange,
  portfolioIsolated, onTogglePortfolioIsolate,
  alphaZoneSize, alphaZoneIsolated, onToggleAlphaZoneIsolate,
  pillarWeights, onPillarWeightChange, onResetWeights, onPillarClick,
  allStocks,
  industriesBySector,
  onCloseMobile,
  highlightedFactor,
  onFactorHighlight,
  highlightedPillar,
  onPillarHighlight,
}: Props) {
  // Shared info-tooltip state — clicking a factor in any panel closes tooltips in others.
  const [infoKey, setInfoKey] = useState<string | null>(null);

  const isDefault = enabledFactors.size === allDefaultEnabled.size &&
    [...allDefaultEnabled].every(k => enabledFactors.has(k));

  return (
    <div className="bg-[#0d1117] border-r border-[#1e293b] w-full h-full min-h-0 flex-shrink-0 flex flex-col">
      {/* Three-zone layout: fixed top (stats + search), scrolling middle (filters,
          factors), Portfolio Events at the bottom of the scroll. */}
      <div className="flex-shrink-0">
        <StatsGrid count={count} factorCount={factorCount} updatedAt={updatedAt} onCloseMobile={onCloseMobile} />
        <SearchBar search={search} onSearch={onSearch} allStocks={allStocks} />
      </div>

      {/* Scrolling middle zone — filters + factor configurators */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <PortfolioPanel
          portfolioSymbols={portfolioSymbols}
          onPortfolioChange={onPortfolioChange}
          portfolioIsolated={portfolioIsolated}
          onTogglePortfolioIsolate={onTogglePortfolioIsolate}
        />

        <AlphaZonePanel
          alphaZoneSize={alphaZoneSize}
          alphaZoneIsolated={alphaZoneIsolated}
          onToggle={onToggleAlphaZoneIsolate}
        />

        <PillarWeightsPanel
          pillarWeights={pillarWeights}
          onPillarWeightChange={onPillarWeightChange}
          onResetWeights={onResetWeights}
          onPillarClick={onPillarClick}
        />

        <MarketCapSlider
          minMarketCap={minMarketCap}
          maxMarketCap={maxMarketCap}
          onMinChange={onMinMarketCapChange}
          onMaxChange={onMaxMarketCapChange}
        />

        <SectorsPanel
          sectors={sectors}
          selectedSectors={selectedSectors}
          onSectorToggle={onSectorToggle}
          selectedIndustries={selectedIndustries}
          onIndustryToggle={onIndustryToggle}
          onSelectAllSectors={onSelectAllSectors}
          industriesBySector={industriesBySector}
        />

        <QualityFactorsPanel
          enabledFactors={enabledFactors}
          onToggleFactor={onToggleFactor}
          pillarWeights={pillarWeights}
          highlightedFactor={highlightedFactor}
          onFactorHighlight={onFactorHighlight}
          highlightedPillar={highlightedPillar}
          onPillarHighlight={onPillarHighlight}
          infoKey={infoKey}
          setInfoKey={setInfoKey}
          canonicalCount={qualityFactorCount}
        />

        <ValuationFactorsPanel
          canonicalCount={valuationFactorCount}
          enabledFactors={enabledFactors}
          onToggleFactor={onToggleFactor}
          highlightedFactor={highlightedFactor}
          onFactorHighlight={onFactorHighlight}
          infoKey={infoKey}
          setInfoKey={setInfoKey}
        />

        <MomentumFactorsPanel
          enabledFactors={enabledFactors}
          onToggleFactor={onToggleFactor}
          highlightedFactor={highlightedFactor}
          onFactorHighlight={onFactorHighlight}
          infoKey={infoKey}
          setInfoKey={setInfoKey}
        />

        {!isDefault && (
          <div className="px-5 py-3">
            <button
              onClick={onResetFactors}
              className="w-full py-1.5 text-[10px] font-semibold uppercase tracking-wider text-blue-400
                         border border-blue-400/30 rounded-lg hover:bg-blue-500/10 transition-colors duration-150 cursor-pointer"
            >
              Reset to Defaults
            </button>
          </div>
        )}

        {/* Home-page alerts panel covers the FULL universe (all 1099 stocks),
            not just portfolio holdings. Per Steve 2026-05-07: the visualizer's
            job is to surface signal across everything we score, not just what
            we own. The /portfolio + /alpha-zone embedded feeds remain scoped
            to their own baskets. */}
        <PortfolioEventsFeed
          symbols={allStocks?.map(s => s.symbol) ?? null}
          title="Universe Alerts"
        />
      </div>
    </div>
  );
}
